import { useEffect, useState } from 'react'
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { primary_color } from '../../../../Helper/uiHelper'
import { request } from '../../../../Redux/Sagas/requests/api'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../../Redux/Reducers/notificationHandling'

const formatApiMessage = (message) => {
  if (!message) return 'Update failed'
  if (typeof message === 'string') return message
  if (typeof message === 'object') return Object.values(message).flat().join(' ')
  return 'Update failed'
}

/**
 * Edit Automatic Code locker: Lock ID always editable; sequence only on planned block (active uses grid Change sequence).
 */
const digitsOnly = (value) => String(value || '').replace(/\D/g, '')

const AutomaticCodeEditForm = ({ defaultValue, onCloseForm }) => {
  const dispatch = useDispatch()
  const [lockIds, setLockIds] = useState([])
  const [blockGroupDynamicId, setBlockGroupDynamicId] = useState(defaultValue?.block_group_dynamic_id || '')
  const [sequence, setSequence] = useState(defaultValue?.code_sequence || 1)
  const initialCode = digitsOnly(defaultValue?.code === '--' ? '' : defaultValue?.code)
  const [code, setCode] = useState(initialCode)
  const [note, setNote] = useState(defaultValue?.note || '')
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [resendEmail, setResendEmail] = useState(true)

  const isPlannedBlock = !!defaultValue?.is_planned_block
  const lockIdChanged =
    blockGroupDynamicId &&
    parseInt(blockGroupDynamicId, 10) !== parseInt(defaultValue?.block_group_dynamic_id || 0, 10)
  const codeChanged = !isPlannedBlock && digitsOnly(code) !== initialCode
  const noteChanged = (note || '') !== (defaultValue?.note || '')
  const codeAffectingChange = lockIdChanged || codeChanged

  useEffect(() => {
    request('automatic-codes/lock-ids', 'GET').then((res) => {
      if (res?.status === 200) {
        setLockIds(res?.data?.data || [])
      }
    })
  }, [])

  const save = (withResend) => {
    setLoading(true)
    const payload = {
      item_id: defaultValue?.id,
      resend_email: !!withResend,
      is_planned_block: isPlannedBlock,
    }

    if (lockIdChanged) {
      payload.block_group_dynamic_id = parseInt(blockGroupDynamicId, 10)
      if (isPlannedBlock) {
        payload.code_sequence = parseInt(sequence, 10) || 1
      }
    } else if (codeChanged) {
      payload.code = digitsOnly(code)
    } else if (isPlannedBlock && sequence) {
      payload.code_sequence = parseInt(sequence, 10)
    }

    if (noteChanged) {
      payload.note = note
    }

    request('automatic-codes/update', 'POST', payload).then((res) => {
      setLoading(false)
      setConfirmOpen(false)
      if (res?.status === 200) {
        dispatch(setNotification({ type: 'success', message: res?.data?.message || 'Updated successfully' }))
        onCloseForm()
      } else {
        dispatch(setNotification({ type: 'error', message: formatApiMessage(res?.data?.message) }))
      }
    })
  }

  const onSubmit = () => {
    if (!lockIdChanged && !codeChanged && !noteChanged && !isPlannedBlock) {
      dispatch(setNotification({ type: 'error', message: 'No changes to save.' }))
      return
    }
    if (lockIdChanged && !blockGroupDynamicId) {
      dispatch(setNotification({ type: 'error', message: 'Please select a Lock ID.' }))
      return
    }
    if (codeChanged && digitsOnly(code).length !== 4) {
      dispatch(setNotification({ type: 'error', message: 'Code must be a 4-digit number.' }))
      return
    }
    if (defaultValue?.is_sold && codeAffectingChange) {
      setConfirmOpen(true)
      return
    }
    save(false)
  }

  return (
    <div className='m-4'>
      <div className='mb-3'>
        <label className='subtitle1 font-weight-bold'>Lock ID</label>
        <Input type='select' value={blockGroupDynamicId} onChange={(e) => setBlockGroupDynamicId(e.target.value)}>
          <option value=''>Select Lock ID</option>
          {lockIds.map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </Input>
      </div>
      {isPlannedBlock ? (
        <div className='mb-3'>
          <label className='subtitle1 font-weight-bold'>Sequence (1–50)</label>
          <Input
            type='number'
            min={1}
            max={50}
            value={sequence}
            disabled={lockIdChanged}
            onChange={(e) => setSequence(e.target.value)}
          />
          {lockIdChanged ? <div className='subtitle1 text-muted mt-1'>Changing Lock ID resets sequence to 1.</div> : null}
        </div>
      ) : null}
      <div className='mb-3'>
        <label className='subtitle1 font-weight-bold'>Notes</label>
        <Input type='textarea' rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {!isPlannedBlock ? (
        <div className='mb-3'>
          <label className='subtitle1 font-weight-bold'>Code</label>
          <Input type='text' inputMode='numeric' maxLength={4} value={code} disabled readOnly />
          <div className='subtitle1 text-muted mt-1'>
            {lockIdChanged
              ? 'Changing Lock ID assigns the catalog code for sequence 1.'
              : 'Assigned from the Automatic Codes catalog via Lock ID (read only).'}
          </div>
        </div>
      ) : null}
      <div className='d-flex justify-content-end mt-5'>
        <button className='btn rounded-pill mt-2 text-danger border-danger mr-3' type='button' onClick={onCloseForm} style={{ fontWeight: 700, width: '12rem' }}>
          Cancel
        </button>
        <button
          className='btn text-white rounded-pill mt-2'
          disabled={loading}
          type='button'
          onClick={onSubmit}
          style={{ background: primary_color, fontWeight: 700, width: '14rem' }}>
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>

      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered style={{ boxShadow: 'none' }}>
        <ModalHeader toggle={() => setConfirmOpen(false)}>Locker has been sold</ModalHeader>
        <ModalBody>
          <div className='mb-2'>Re-issue confirmation email with the new code?</div>
          <label className='d-block'>
            <input type='radio' checked={resendEmail} onChange={() => setResendEmail(true)} /> Re Issue Confirmation Email with New Code
          </label>
          <label className='d-block'>
            <input type='radio' checked={!resendEmail} onChange={() => setResendEmail(false)} /> Do Not reissue Confirmation email
          </label>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button color='primary' style={{ background: primary_color, border: 'none' }} onClick={() => save(resendEmail)}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default AutomaticCodeEditForm
